import { fetcher } from '@chipinside/fetcher'
import { LoadingIconButton } from '@chipinside/frontend'
import { PageBuilderActionProps } from '@chipinside/pagebuilder/dist/types'
import { Translate } from '@chipinside/translate'
import { Menu, MenuItem, Typography } from '@mui/material'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { useFormBuilder } from '#/store/hooks/useFormBuilder'
import {
  PageBuilderIcon,
  PageBuilderIconProps,
} from '#/store/providers/Pagebuilder/Components/Icon'
import Colors from '#/styles/Colors'
import { listeners } from '#/utils/listeners'

export type PageBuilderDropdownActionsProps = {
  actions: PageBuilderActionProps[]
  icon: PageBuilderIconProps
  title: string
  slots?: Record<string, any>
}

export function PageBuilderDropdownActions(props: PageBuilderDropdownActionsProps) {
  let { actions, icon, title } = props

  const { build, confirm } = useFormBuilder()

  const [isLoading, setIsLoading] = useState(false)

  const refresh = () => window.dispatchEvent(new Event(listeners.pagebuilder))

  const handleAction = (e: any, action: any) => {
    e.stopPropagation()
    e.preventDefault()
    const { actionType, form, id, level, message, save, subtitle, title, width } = action

    const actionProps = props.slots?.[id] ?? {}

    switch (actionType) {
      case 'form': {
        const params = {
          controller: form,
          level: ['warning', 'error', 'danger'].includes(level) ? level : 'primary',
          save,
          subtitle,
          title,
          width,
        }
        build({
          ...actionProps,
          ...props,
          afterSuccess: () => refresh(),
          formName: 'page_builder',
          formType: 'action',
          params,
        })
        break
      }
      case 'confirm': {
        const params = {
          level: ['error', 'danger'].includes(level) ? 'error' : 'warning',
          message,
          save,
          subtitle,
          title,
        }
        confirm({
          ...actionProps,
          ...props,
          afterSuccess: () => refresh(),
          formName: 'page_builder',
          formType: 'action',
          params,
        })
        break
      }
      case 'submit': {
        setIsLoading(true)
        const promise = fetcher(save)
        toast.promise(promise, {
          error: (response: any) => {
            const errorMessage = Translate({ messageKey: 'something_went_wrong' })
            return response?.data?.error?.message ?? errorMessage
          },
          finally: () => {
            setIsLoading(false)
          },
          loading: Translate({ messageKey: 'loading' }),
          success: (response: any) => {
            refresh()
            const successMessage = Translate({ messageKey: 'action_completed' })
            return response?.data?.message ?? successMessage
          },
        })
      }
    }
  }

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    setAnchorEl(e.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleActionClick = (e: any, action: any) => {
    handleClose()
    handleAction(e, action)
  }

  return (
    <div onClick={e => e.stopPropagation()}>
      <LoadingIconButton
        title={title}
        size={'small'}
        loading={isLoading}
        disabled={isLoading}
        icon={<PageBuilderIcon disabled={isLoading} {...icon} />}
        onClick={handleClick}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        elevation={1}
      >
        {actions.map(action => {
          return (
            <MenuItem
              key={action.title}
              onClick={e => handleActionClick(e, action)}
              sx={{ minWidth: 150 }}
            >
              <Typography
                color={Colors.feedback[action.level ?? 'regular']?.color}
                sx={{ display: 'flex', gap: 1 }}
              >
                {action.title}
              </Typography>
            </MenuItem>
          )
        })}
      </Menu>
    </div>
  )
}
